import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import { useLoginMutation } from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setEnableAuth } from "../../Store/Auth/AuthSlice";
import { useNavigate } from "react-router-dom";
import { Privileges } from "../../Core/Enum/enum";
import useIsMobile from "../../Core/hook/useIsMobile";
import { swapToShippedLogo } from "../../Core/branding/brandLogo";

// White-label palette. Reads the CSS variables published by useCompanyBoot
// (`--brand-primary`, `--brand-secondary`, `--brand-accent`) so a tenant's
// configured colors flow through without a rebuild. Falls back to a neutral
// palette (never another tenant's brand). Tenants set these via Company
// Settings > Brand Colors.
const BRAND      = "var(--brand-primary, #4f46e5)";
const BRAND_DARK = "var(--brand-secondary, #3730a3)";
const NAVY       = "var(--brand-accent, #10233F)";

// Shared field styles: static top label + placeholder only, so browser autofill
// never collides with a floating MUI label (root cause of the "merged text"
// bug). Chrome autofill background is overridden so autofilled values remain
// legible on the white card.
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    height: 50,
    fontSize: 15,
    background: "#fff",
    "& fieldset": { borderColor: "#d8dee5" },
    "&:hover fieldset": { borderColor: "#b6c1cc" },
    "&.Mui-focused fieldset": { borderColor: BRAND, borderWidth: 1.5 },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 14px",
  },
  // Chrome/Edge autofill: keep the pale background and dark text instead of
  // the browser's yellow overlay that used to sit on top of the label.
  "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
    WebkitBoxShadow: "0 0 0 1000px #fff inset",
    WebkitTextFillColor: "#1f2937",
    caretColor: "#1f2937",
    transition: "background-color 5000s ease-in-out 0s",
  },
} as const;

const FieldLabel: React.FC<{ children: React.ReactNode; htmlFor: string }> = ({ children, htmlFor }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", mb: 0.75 }}
  >
    {children}
  </Typography>
);

interface LoginProps {
  /** Full horizontal Step Up Academy logo (emblem + wordmark). Rendered
   *  centered directly above "Welcome Back". Falls back to no logo when
   *  the parent hasn't loaded the branding config yet. */
  logoSrc?: string;
}

const Login: React.FC<LoginProps> = ({ logoSrc }) => {
  const dispatch = useAppDispatch();
  const { showErrorMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const isMobileRes = useIsMobile();
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Fresh-login transition guard.
   *
   * Set by the reset-password "Continue to Staff/Parent Login" button
   * as a sessionStorage flag right before the hard redirect. When the
   * public login page mounts and detects it:
   *
   *   1. Set autocomplete="off" on the form + password field so the
   *      browser password manager does NOT autofill the previously
   *      authenticated Super Admin credentials into a Staff sign-in.
   *   2. Force both formik values to "" and set an attempt of clearing
   *      any autofill the browser may have already injected before our
   *      autocomplete override took effect.
   *   3. Delete the flag so a normal future visit to /login gets
   *      normal password-manager behavior.
   *
   * Normal /login visits (no flag) keep autocomplete="on" so real
   * users' saved credentials continue to work.
   */
  const isFreshLoginRef = useRef<boolean>(false);
  try {
    if (typeof window !== "undefined") {
      const flag = window.sessionStorage.getItem("forceBlankLogin");
      if (flag === "1") isFreshLoginRef.current = true;
    }
  } catch { /* private mode / storage disabled */ }
  const isFreshLogin = isFreshLoginRef.current;

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: async (values) => {
      try {
        const loginResponse = await login(values).unwrap();
        if (!loginResponse.status) {
          showErrorMessage(loginResponse.message);
        } else {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          localStorage.setItem("backgroundImg", tokenData.backgroundImg);

          if (tokenData.lightThemeColor) {
            localStorage.setItem("lightThemeColor", JSON.stringify(tokenData.lightThemeColor));
          }
          if (tokenData.darkThemeColor) {
            localStorage.setItem("darkThemeColor", JSON.stringify(tokenData.darkThemeColor));
          }
          dispatch(setEnableAuth());

          const rawData = loginResponse.data as any;
          const userType: string = rawData?.user?.user_type || "employee";
          const roleName: string = String(rawData?.user?.user_role_name || rawData?.user_role_name || "").toLowerCase();

          navigateBasedOnPrivileges(loginResponse.privileges, userType, roleName);
          window.location.reload();
        }
      } catch (error: any) {
        // Route toast copy off the backend's machine-readable `reason`
        // field (spec §9). Never substring-match `message` — that
        // caused the "suspended" toast to appear for bad-password
        // retries when a suspended user typed a wrong password.
        const backendMsg: string | undefined = error?.data?.message;
        const reason: string | undefined = error?.data?.reason;
        const httpStatus: number | undefined = error?.status ?? error?.originalStatus;

        if (reason === "suspended") {
          showErrorMessage(backendMsg || "Your Staff Portal access has been suspended. Please contact your administrator.");
        } else if (reason === "invitation_pending") {
          showErrorMessage(backendMsg || "Your account setup is not complete. Please use the setup email sent to you.");
        } else if (reason === "invalid_credentials" || httpStatus === 401) {
          showErrorMessage(backendMsg || "The username or password is incorrect.");
        } else if (httpStatus === 403) {
          // Genuine 403 without a known reason — surface backend copy.
          showErrorMessage(backendMsg || "Your account cannot sign in at this time. Please contact your administrator.");
        } else {
          showErrorMessage(backendMsg || "Unable to sign in. Please check your username and password.");
        }
      }
    },
  });

  // Fresh-login effect: uses `formik.setValues`, so it must appear
  // AFTER formik is declared. Blanks the form on mount when the
  // `forceBlankLogin` flag was set by the reset-Continue handler.
  useEffect(() => {
    if (!isFreshLogin) return;
    formik.setValues({ username: "", password: "" });
    setShowPassword(false);
    try { window.sessionStorage.removeItem("forceBlankLogin"); } catch { /* noop */ }
    // Second pass ~50ms later — some browsers autofill AFTER React
    // paints and would otherwise clobber the blank state.
    const t = window.setTimeout(() => {
      formik.setValues({ username: "", password: "" });
    }, 50);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreshLogin]);

  const formValid = useMemo(
    () => !!(formik.values.username && formik.values.password),
    [formik.values.username, formik.values.password]
  );

  const navigateBasedOnPrivileges = (
    privileges: string[],
    userType: string = "employee",
    roleName: string = ""
  ) => {
    // Portal separation, Phase 1. Route audience is the primary
    // decision; the privilege fallback ladder only runs for users
    // that don't belong to a first-class portal.
    //   parent user            → Parent Portal
    //   System_Admin holder    → Admin Dashboard
    //   seeded "Staff" role    → Staff Portal (never Admin Dashboard)
    //   anything else          → existing legacy privilege ladder
    if (userType === "parent") {
      navigate("/parent-portal");
      return;
    }
    if (privileges.includes("System_Admin")) {
      navigate("/dashboard");
      return;
    }
    if (roleName === "staff") {
      navigate("/staff-portal");
      return;
    }
    if (privileges.includes(Privileges.DashboardView)) navigate("/dashboard");
    else if (privileges.includes(Privileges.DepartmentView)) navigate("/department/list");
    else if (privileges.includes(Privileges.UserView)) navigate("/user/list");
    else if (privileges.includes(Privileges.SettingView)) navigate("/setting");
    else if (privileges.includes(Privileges.PrivilegeView)) navigate("/user/privilege/list");
    else if (privileges.includes(Privileges.UserRoleView)) navigate("/user/role/list");
    else if (privileges.includes(Privileges.StaffView)) navigate("/team/list");
    else if (privileges.includes(Privileges.PositionView)) navigate("/position/list");
    else if (privileges.includes(Privileges.ScheduleView)) navigate("/schedule/list");
    else if (privileges.includes(Privileges.PayrollView)) navigate("/payroll/list");
    else if (privileges.includes(Privileges.EmailView)) navigate("/email/setting/list");
  };

  return (
    <form onSubmit={formik.handleSubmit} autoComplete={isFreshLogin ? "off" : "on"}>
      <Box
        sx={{
          background: "#ffffff",
          boxShadow: "0 24px 60px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.05)",
          borderRadius: "24px",
          // Extra top padding so the horizontal logo has breathing room
          // above "Welcome Back". Desktop card: pt=6.5 (~52px) instead of 6.
          pt: isMobileRes ? 3.5 : 6.5,
          pb: isMobileRes ? 3.5 : 6,
          px: isMobileRes ? 3.5 : 6,
          maxWidth: 480,
          width: "100%",
          mx: "auto",
        }}
      >
        {/* ── Step Up Academy horizontal logo ────────────────────────
            Centered directly above "Welcome Back". Uses the same
            company-logo asset that used to sit on the left panel.
            Responsive width: 240px on desktop (in-spec range 220–260),
            scales down cleanly on tablet/mobile without stretching.
            No pointer events — it's decorative, never a login target. */}
        {logoSrc && (
          <Box sx={{ textAlign: "center", mb: 2.5 }}>
            <Box
              component="img"
              src={logoSrc}
              alt="Company Logo"
              // If the API-supplied logo URL 404s / times out (stale
              // environment path, wrong host), fall back once to the
              // shipped Step Up Academy asset via the shared
              // brand-logo helper. Guarded against infinite loops.
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                swapToShippedLogo(e.currentTarget as HTMLImageElement);
              }}
              sx={{
                display: "inline-block",
                width: { xs: 180, sm: 220, md: 240 },
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          </Box>
        )}

        {/* Title */}
        <Box sx={{ textAlign: "center", mb: 3.5 }}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: NAVY, letterSpacing: -0.2 }}>
            Welcome Back
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
            Please login to your account
          </Typography>
        </Box>

        {/* Username */}
        <Box sx={{ mb: 2 }}>
          <FieldLabel htmlFor="login-username">Email or Username</FieldLabel>
          <TextField
            id="login-username"
            fullWidth
            variant="outlined"
            type="text"
            placeholder="Enter your email or username"
            autoComplete={isFreshLogin ? "off" : "username"}
            inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
            sx={fieldSx}
            {...formik.getFieldProps("username")}
          />
        </Box>

        {/* Password */}
        <Box sx={{ mb: 1 }}>
          <FieldLabel htmlFor="login-password">Password</FieldLabel>
          <TextField
            id="login-password"
            fullWidth
            variant="outlined"
            placeholder="Enter your password"
            type={showPassword ? "text" : "password"}
            autoComplete={isFreshLogin ? "new-password" : "current-password"}
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...formik.getFieldProps("password")}
          />
        </Box>

        <Box sx={{ textAlign: "right", mb: 2.5 }}>
          <Typography
            component="span"
            onClick={() => navigate("/forgot-password")}
            sx={{
              fontSize: 13,
              color: BRAND,
              cursor: "pointer",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Forgot password?
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          disabled={!formValid || isLoading}
          type="submit"
          sx={{
            py: 1.5,
            borderRadius: "10px",
            fontWeight: 700,
            fontSize: 15,
            textTransform: "none",
            backgroundColor: BRAND,
            color: "#ffffff",
            boxShadow: "0 8px 20px rgba(85,166,70,0.30)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: BRAND_DARK,
              boxShadow: "0 10px 24px rgba(85,166,70,0.45)",
            },
            "&:focus-visible": {
              outline: `3px solid ${BRAND_DARK}55`,
              outlineOffset: 2,
            },
            "&:active": { transform: "scale(0.99)" },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
              color: "#94a3b8",
              boxShadow: "none",
            },
          }}
        >
          {isLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Login"}
        </Button>
      </Box>
    </form>
  );
};

export default React.memo(Login);
